import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Search, Upload, Sparkles, Download, RefreshCw, RotateCcw, ArrowLeft, ImagePlus, X, Wand2, Check, Instagram, ClipboardPaste } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useFeatures, isFeatureEnabled } from "@/lib/features";
import { readClipboardImageFile, imageFileFromPasteEvent, isSupportedImage } from "@/lib/clipboardImage";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const ASSIST_ACTIONS = [
  { k: "fix_grammar", l: "Fix Grammar" }, { k: "spell_check", l: "Spell Check" },
  { k: "shorter", l: "Make Shorter" }, { k: "crisp", l: "Make Crisp" },
  { k: "creative", l: "Make Creative" }, { k: "rewrite", l: "Rewrite" },
  { k: "headline", l: "Generate Headline" }, { k: "caption", l: "Generate Caption" },
];

// Inline text field + AI Assist (never silently overwrites — shows a suggestion first).
function AssistField({ label, testid, value, onChange, textarea, placeholder }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const run = async (action) => {
    setBusy(action); setSuggestion(null);
    try { const { suggestion: s } = await api.igTextAssist(action, value); setSuggestion({ action, text: s }); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI Assist failed."); }
    setBusy(null);
  };
  const Field = textarea ? "textarea" : "input";
  return (
    <div data-testid={`ig-field-${testid}`}>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-semibold text-[#A1A1AA]">{label}</label>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`ig-assist-toggle-${testid}`}
          className="flex items-center gap-1 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-2 py-0.5 text-[10px] font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20"><Wand2 size={11} /> AI Assist</button>
      </div>
      <Field value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`ig-input-${testid}`}
        rows={textarea ? 4 : undefined}
        className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
      {open && (
        <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.03] p-2" data-testid={`ig-assist-panel-${testid}`}>
          <div className="flex flex-wrap gap-1.5">
            {ASSIST_ACTIONS.map((a) => (
              <button key={a.k} type="button" onClick={() => run(a.k)} disabled={!!busy} data-testid={`ig-assist-${a.k}-${testid}`}
                className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-40">
                {busy === a.k ? <Loader2 size={11} className="animate-spin" /> : a.l}
              </button>
            ))}
          </div>
          {suggestion && (
            <div className="mt-2 rounded-lg border border-[#8B5CF6]/30 bg-black/40 p-2" data-testid={`ig-assist-suggestion-${testid}`}>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#a78bfa]">Suggestion</div>
              <div className="whitespace-pre-wrap text-sm text-white">{suggestion.text}</div>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => { onChange(suggestion.text); setSuggestion(null); toast.success("Applied"); }} data-testid={`ig-assist-use-${testid}`}
                  className="flex items-center gap-1 rounded-lg bg-[#8B5CF6] px-2.5 py-1 text-[11px] font-bold text-white"><Check size={12} /> Use Suggestion</button>
                <button type="button" onClick={() => setSuggestion(null)} data-testid={`ig-assist-keep-${testid}`}
                  className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-semibold text-[#A1A1AA]">Keep Original</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImageDrop({ label, testid, role, value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return; setBusy(true);
    try { const res = await api.igUpload(file, role); onChange(res); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    setBusy(false);
  };
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">{label}</label>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`ig-upload-${testid}`}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {value ? (
        <div className="relative inline-block">
          <img src={abs(value.url)} alt={label} className="h-20 w-20 rounded-lg border border-white/15 object-cover" data-testid={`ig-preview-${testid}`} />
          <button onClick={() => onChange(null)} data-testid={`ig-remove-${testid}`} className="absolute -right-2 -top-2 rounded-full bg-red-500 p-0.5 text-white"><X size={12} /></button>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`ig-pick-${testid}`}
          className="flex h-20 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 text-xs font-semibold text-[#A1A1AA] hover:border-[#8B5CF6]/60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} /> {label}</>}
        </button>
      )}
    </div>
  );
}

const PROGRESS_STEPS = ["Preparing references", "Building composition", "Generating image", "Finalising design"];

export default function InstagramPostMaker() {
  useFeatures();
  const [cfg, setCfg] = useState(null);
  const [featureOn, setFeatureOn] = useState(true);
  const [stage, setStage] = useState("library"); // library | studio | generating | result
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [tpl, setTpl] = useState(null);

  // studio inputs
  const [logo, setLogo] = useState(null);
  const [main, setMain] = useState(null);
  const [refs, setRefs] = useState([]);
  const [headline, setHeadline] = useState("");
  const [text, setText] = useState("");
  const [caption, setCaption] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [useStd, setUseStd] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [showStd, setShowStd] = useState(false);
  const refReplaceRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [brandKit, setBrandKit] = useState({ logo_path: null, logo_url: null, colors: [] });
  const [brandColors, setBrandColors] = useState(["#E1306C", "#0b2e6b", "#FFE800"]);

  useEffect(() => {
    api.igConfig().then((c) => { setCfg(c); setFeatureOn(c.enabled); if (c.aspects?.length) setAspect(c.aspects[0].key); }).catch(() => {});
    api.igGetBrandKit().then((k) => { setBrandKit(k); if (k.colors?.length) setBrandColors([...k.colors, "#E1306C", "#0b2e6b", "#FFE800"].slice(0, 3)); }).catch(() => {});
  }, []);

  const loadTemplates = useCallback(async () => {
    if (!featureOn) return;
    setLoadingTpl(true);
    try {
      const [c, t] = await Promise.all([api.igCategories(), api.igTemplates(activeCat, q)]);
      setCats(c); setTemplates(t);
    } catch { /* feature may be off */ }
    setLoadingTpl(false);
  }, [activeCat, q, featureOn]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const chooseTemplate = (t) => { setTpl(t); setAspect(t.recommended_aspect || aspect); if (!logo && brandKit.logo_path) setLogo({ path: brandKit.logo_path, url: brandKit.logo_url }); setStage("studio"); };

  const addRef = async (file) => {
    if (!file) return;
    try { const res = await api.igUpload(file, "reference"); setRefs((r) => [...r, res]); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };

  const generate = async () => {
    if (!tpl) return;
    setStage("generating"); setProgress(0); setResult(null);
    const timer = setInterval(() => setProgress((p) => Math.min(p + 1, PROGRESS_STEPS.length - 1)), 2200);
    try {
      const res = await api.igGenerate({
        template_source: tpl.source === "user_reference" ? "user_reference" : "admin_template",
        template_id: tpl.source === "user_reference" ? null : tpl.id,
        template_path: tpl.source === "user_reference" ? tpl.path : null,
        aspect, headline, text, caption, prompt,
        logo_path: logo?.path || null, main_path: main?.path || null,
        ref_paths: refs.map((r) => r.path),
        brand_colors: brandKit.colors && brandKit.colors.length ? brandKit.colors : undefined,
      });
      clearInterval(timer); setProgress(PROGRESS_STEPS.length);
      setResult(res);
      setHistory((h) => [{ ...res, template_name: tpl.name, at: new Date() }, ...h].slice(0, 8));
      setStage("result");
    } catch (e) {
      clearInterval(timer);
      toast.error(e?.response?.data?.detail || "Generation failed. Please try again.");
      setStage("studio");
    }
  };

  const download = async (url, generationId) => {
    try {
      const r = await fetch(abs(url), { credentials: "include" });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `instagram-post-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      // Register the download into the admin review queue (non-blocking — never fail the download).
      if (generationId) api.igRegisterDownload(generationId).catch(() => {});
    } catch { toast.error("Download failed."); }
  };

  const uploadReferenceTemplate = async (file, origin = "upload") => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Clipboard content isn't a supported image."); return; }
    try {
      const res = await api.igUpload(file, "template");
      if (!logo && brandKit.logo_path) setLogo({ path: brandKit.logo_path, url: brandKit.logo_url });
      setTpl({ id: "user_reference", name: origin === "clipboard" ? "Pasted from clipboard" : (file.name || "Your reference"), image_url: res.url, path: res.path, source: "user_reference", origin, recommended_aspect: aspect });
      setStage("studio");
      if (origin === "clipboard") toast.success("Pasted image added as reference");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };

  const pasteReferenceFromClipboard = async () => {
    try { const file = await readClipboardImageFile(); await uploadReferenceTemplate(file, "clipboard"); }
    catch (e) { toast.error(e?.message || "Clipboard paste failed."); }
  };

  // Keyboard paste (Ctrl/Cmd+V) works anywhere while the Post Maker is open.
  const pasteHandlerRef = useRef();
  pasteHandlerRef.current = (e) => {
    if (stage === "generating") return;
    let file;
    try { file = imageFileFromPasteEvent(e); } catch (err) { toast.error(err.message); return; }
    if (file) { e.preventDefault(); uploadReferenceTemplate(file, "clipboard"); }
  };
  useEffect(() => {
    const fn = (e) => pasteHandlerRef.current && pasteHandlerRef.current(e);
    window.addEventListener("paste", fn);
    return () => window.removeEventListener("paste", fn);
  }, []);

  const saveBrandKit = async () => {
    try {
      const k = await api.igSaveBrandKit({ logo_path: logo?.path || brandKit.logo_path, logo_url: logo?.url || brandKit.logo_url, colors: brandColors });
      setBrandKit(k);
      toast.success("Brand Kit saved — it'll auto-apply to your posts");
    } catch { toast.error("Could not save Brand Kit."); }
  };

  const startAgain = () => { setTpl(null); setLogo(null); setMain(null); setRefs([]); setHeadline(""); setText(""); setCaption(""); setPrompt(""); setResult(null); setStage("library"); };

  if (!featureOn) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-32 text-center" data-testid="ig-unavailable">
          <Instagram size={40} className="mb-4 text-[#E1306C]" />
          <h1 className="font-display text-2xl font-black text-white">Instagram Post Maker is unavailable</h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">This feature is currently turned off. Please check back later.</p>
        </div>
      </div>
    );
  }

  const aspects = cfg?.aspects || [{ key: "1:1", w: 1080, h: 1080 }];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-5 py-8" data-testid="instagram-post-maker">
        {/* ---------------- LIBRARY ---------------- */}
        {stage === "library" && (
          <>
            <div className="mb-6">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[#E1306C]/40 bg-[#E1306C]/10 px-3 py-1 text-xs font-bold text-[#f48fb1]"><Instagram size={13} /> AI Creative Studio</div>
              <h1 className="font-display text-4xl font-black tracking-tight text-white sm:text-5xl">Instagram Post Maker</h1>
              <p className="mt-2 max-w-xl text-sm text-[#A1A1AA] sm:text-base">Create professional Instagram posts from templates using AI. Pick a template, add your content, and generate.</p>
            </div>
            <div className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="ig-template-source">
              <div className="rounded-xl border border-dashed border-[#8B5CF6]/40 bg-[#8B5CF6]/[0.04] p-5">
                <div className="mb-3 text-sm font-bold text-white">Reference Template</div>
                <label className="mb-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-3 py-2.5 text-sm font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20">
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="ig-upload-reference-template"
                    onChange={(e) => { uploadReferenceTemplate(e.target.files?.[0], "upload"); e.target.value = ""; }} />
                  <Upload size={16} /> Upload Reference Template
                </label>
                <button type="button" onClick={pasteReferenceFromClipboard} data-testid="ig-paste-reference-template"
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2.5 text-sm font-bold text-white hover:border-[#8B5CF6]/60">
                  <ClipboardPaste size={16} /> Paste From Clipboard
                </button>
                <div className="mt-2 text-[11px] text-[#A1A1AA]">Use your own image as the AI reference (PNG / JPG / WEBP). You can also press Ctrl / Cmd + V anywhere.</div>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center">
                <Instagram size={22} className="text-[#E1306C]" />
                <div className="text-sm font-bold text-white">Pick From Our Templates</div>
                <div className="text-xs text-[#A1A1AA]">Browse the ready-made template library below</div>
              </div>
            </div>

            {cfg?.templates_enabled === false ? null : (<>
            <div className="mb-2 text-sm font-bold uppercase tracking-widest text-[#A1A1AA]">Our Templates</div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <Search size={15} className="text-[#666]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" data-testid="ig-template-search"
                  className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#666]" />
              </div>
            </div>
            <div className="mb-5 flex flex-wrap gap-2" data-testid="ig-category-filter">
              {["All", ...cats.map((c) => c.name)].map((c) => (
                <button key={c} onClick={() => setActiveCat(c)} data-testid={`ig-cat-${c}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${activeCat === c ? "bg-[#E1306C] text-white" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{c}</button>
              ))}
            </div>
            {loadingTpl ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E1306C]" /></div>
            ) : templates.length === 0 ? (
              <div className="py-20 text-center text-sm text-[#666]" data-testid="ig-no-templates">No templates yet in this category. An admin can add templates from the dashboard.</div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="ig-template-grid">
                {templates.map((t) => (
                  <div key={t.id} data-testid={`ig-template-${t.id}`} className="group overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
                    <div className="aspect-square overflow-hidden bg-black">
                      <img src={abs(t.thumbnail_url || t.image_url)} alt={t.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    </div>
                    <div className="p-3">
                      <div className="truncate text-sm font-bold text-white">{t.name}</div>
                      <div className="text-[11px] text-[#666]">{t.category} · {t.recommended_aspect}</div>
                      <button onClick={() => chooseTemplate(t)} data-testid={`ig-use-template-${t.id}`}
                        className="mt-2 w-full rounded-lg bg-[#E1306C] py-1.5 text-xs font-bold text-white hover:bg-[#E1306C]/90">Use Template</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </>)}
          </>
        )}

        {/* ---------------- STUDIO ---------------- */}
        {stage === "studio" && tpl && (
          <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
            <div>
              <button onClick={() => setStage("library")} data-testid="ig-back-to-library" className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><ArrowLeft size={14} /> Back to templates</button>
              <div className="overflow-hidden rounded-xl border border-white/10" data-testid="ig-selected-template">
                <img src={abs(tpl.image_url)} alt={tpl.name} className="w-full object-cover" />
              </div>
              <div className="mt-2 text-sm font-bold text-white">{tpl.name}</div>
              <div className="text-[11px] text-[#666]">{tpl.source === "user_reference" ? (tpl.origin === "clipboard" ? "Pasted from clipboard" : "Your uploaded reference") : tpl.category}</div>
              {tpl.source === "user_reference" && (
                <div className="mt-2 flex gap-2" data-testid="ig-reference-actions">
                  <input ref={refReplaceRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => { uploadReferenceTemplate(e.target.files?.[0], "upload"); e.target.value = ""; }} />
                  <button onClick={() => refReplaceRef.current?.click()} data-testid="ig-reference-replace" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#8B5CF6]/60">Replace</button>
                  <button onClick={pasteReferenceFromClipboard} data-testid="ig-reference-paste" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#8B5CF6]/60">Paste</button>
                  <button onClick={() => { setTpl(null); setStage("library"); }} data-testid="ig-reference-remove" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-red-400">Remove</button>
                </div>
              )}
            </div>

            <div className="space-y-5">
              <h2 className="font-display text-xl font-black text-white">Customize this post</h2>

              <div className="rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/[0.05] p-3" data-testid="ig-brand-kit">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-bold text-white">Brand Kit {brandKit.colors?.length ? <span className="ml-1 rounded-full bg-[#8B5CF6]/25 px-1.5 py-0.5 text-[9px] text-[#c4b5fd]">auto-applied</span> : null}</div>
                  <button onClick={saveBrandKit} data-testid="ig-brand-kit-save" className="rounded-lg bg-[#8B5CF6] px-2.5 py-1 text-[11px] font-bold text-white">Save Brand Kit</button>
                </div>
                <div className="flex items-center gap-3">
                  {brandKit.logo_url ? <img src={abs(brandKit.logo_url)} alt="brand logo" className="h-10 w-10 rounded-lg border border-white/15 object-contain bg-black/30" data-testid="ig-brand-logo" /> : <span className="text-[11px] text-[#A1A1AA]">Upload a logo below, then Save.</span>}
                  <div className="flex items-center gap-1.5">
                    {brandColors.map((c, i) => (
                      <input key={i} type="color" value={c} data-testid={`ig-brand-color-${i}`} onChange={(e) => setBrandColors((x) => x.map((v, j) => (j === i ? e.target.value : v)))}
                        className="h-7 w-7 cursor-pointer rounded border border-white/15 bg-transparent" title={`Brand colour ${i + 1}`} />
                    ))}
                  </div>
                </div>
                <div className="mt-1.5 text-[10px] text-[#A1A1AA]">Your saved logo + colours are automatically applied to every post you generate.</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <ImageDrop label="Logo" testid="logo" role="logo" value={logo} onChange={setLogo} />
                <ImageDrop label="Main Image" testid="main" role="main" value={main} onChange={setMain} />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Reference Images (optional)</label>
                <div className="flex flex-wrap items-center gap-2" data-testid="ig-refs">
                  {refs.map((r, i) => (
                    <div key={i} className="relative">
                      <img src={abs(r.url)} alt="ref" className="h-16 w-16 rounded-lg border border-white/15 object-cover" />
                      <button onClick={() => setRefs((x) => x.filter((_, j) => j !== i))} data-testid={`ig-remove-ref-${i}`} className="absolute -right-2 -top-2 rounded-full bg-red-500 p-0.5 text-white"><X size={11} /></button>
                    </div>
                  ))}
                  {refs.length < 4 && (
                    <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-white/20 text-[#A1A1AA] hover:border-[#8B5CF6]/60">
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="ig-add-ref" onChange={(e) => { addRef(e.target.files?.[0]); e.target.value = ""; }} />
                      <ImagePlus size={16} />
                    </label>
                  )}
                </div>
              </div>

              <AssistField label="Headline (optional)" testid="headline" value={headline} onChange={setHeadline} placeholder="e.g. INDIA WIN THE SERIES" />
              <AssistField label="Text / Information" testid="text" value={text} onChange={setText} textarea placeholder="Enter your post text (any language — Telugu / Hindi / English…)" />
              <AssistField label="Caption (optional)" testid="caption" value={caption} onChange={setCaption} textarea placeholder="Optional supporting caption" />

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#A1A1AA]">Aspect Ratio (choose for this generation)</label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ig-aspect-select">
                  {aspects.map((a) => (
                    <button key={a.key} onClick={() => setAspect(a.key)} data-testid={`ig-aspect-${a.key}`}
                      className={`rounded-lg border px-2 py-2 text-center ${aspect === a.key ? "border-[#E1306C] bg-[#E1306C]/10 text-white" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>
                      <div className="text-sm font-bold">{a.key}</div>
                      <div className="text-[10px] text-[#666]">{a.w}×{a.h}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <button onClick={() => setShowStd((v) => !v)} data-testid="ig-toggle-std-prompt" className="flex w-full items-center justify-between text-sm font-semibold text-white">
                  AI Generation Instructions <Sparkles size={14} className="text-[#a78bfa]" />
                </button>
                {showStd && (
                  <div className="mt-2 space-y-2" data-testid="ig-std-prompt-panel">
                    <div className="rounded-lg bg-black/30 p-2 text-[11px] leading-relaxed text-[#A1A1AA]">
                      The AI will preserve this template's composition and visual style while replacing its content with your supplied logo, images and text — keeping your language and facts exact.
                    </div>
                    <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ig-custom-prompt" rows={3}
                      placeholder="Additional instructions (optional) — e.g. make the player more prominent, darker background"
                      className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
                  </div>
                )}
              </div>

              <button onClick={generate} data-testid="ig-create-post-btn"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#E1306C] to-[#F77737] py-3.5 text-sm font-black text-white hover:opacity-90">
                <Sparkles size={17} /> Create Post
              </button>
            </div>
          </div>
        )}

        {/* ---------------- GENERATING ---------------- */}
        {stage === "generating" && (
          <div className="flex flex-col items-center py-24" data-testid="ig-generating">
            <Loader2 size={40} className="animate-spin text-[#E1306C]" />
            <h2 className="mt-6 font-display text-xl font-black text-white">Creating your Instagram post…</h2>
            <div className="mt-6 w-full max-w-sm space-y-2">
              {PROGRESS_STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-2 text-sm">
                  {i < progress ? <Check size={16} className="text-[#22c55e]" /> : i === progress ? <Loader2 size={16} className="animate-spin text-[#E1306C]" /> : <div className="h-4 w-4 rounded-full border border-white/20" />}
                  <span className={i <= progress ? "text-white" : "text-[#555]"}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---------------- RESULT ---------------- */}
        {stage === "result" && result && (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="flex flex-col items-center">
              <div className="overflow-hidden rounded-xl border border-white/10 bg-black" style={{ maxWidth: 520 }}>
                <img src={abs(result.url)} alt="Generated post" className="w-full object-contain" data-testid="ig-result-image" />
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <button onClick={() => download(result.url, result.generation_id)} data-testid="ig-download-btn" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-4 py-2 text-sm font-bold text-white hover:bg-[#E1306C]/90"><Download size={15} /> Download</button>
                <button onClick={() => setStage("studio")} data-testid="ig-back-to-edit-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-white/30"><ArrowLeft size={15} /> Back to Edit</button>
                <button onClick={() => { setStage("studio"); setTimeout(generate, 50); }} data-testid="ig-regenerate-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-white/30"><RefreshCw size={15} /> Generate Again</button>
                <button onClick={startAgain} data-testid="ig-start-again-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white"><RotateCcw size={15} /> Start Again</button>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-bold text-white">This session</h3>
              <div className="space-y-2" data-testid="ig-history">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-2">
                    <img src={abs(h.url)} alt="" className="h-12 w-12 rounded object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-white">{h.template_name}</div>
                      <div className="text-[10px] text-[#666]">{h.aspect} · {new Date(h.at).toLocaleTimeString()}</div>
                    </div>
                    <button onClick={() => download(h.url, h.generation_id)} className="rounded p-1 text-[#A1A1AA] hover:text-white"><Download size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
